import {
  alpha,
  Box,
  IconButton,
  IconButtonProps,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { Check as CheckIcon, FileCopy as CopyIcon } from "@mui/icons-material";

interface CodeCopyButtonProps {
  dataId?: string | undefined;
  justCopied: boolean;
  onClick: IconButtonProps["onClick"];
  isVisible?: boolean;
  onMouseEnter: () => void;
}

const CodeCopyButton = ({
  dataId,
  justCopied,
  onClick,
  isVisible = true,
  onMouseEnter,
}: CodeCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const onCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsCopied(true);
    onClick?.(e);
    setTimeout(() => setIsCopied(false), 800);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: (theme) => theme.spacing(1),
        right: (theme) => theme.spacing(1),
        background: (theme) => theme.palette.code.background,
        borderRadius: (theme) => theme.spacing(0.5),
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      <Tooltip
        title={isCopied ? "Copied!" : "Copy to clipboard"}
        arrow
        placement="top"
      >
        <IconButton
          data-name={
            dataId ? `copy-to-clipboard-${dataId}` : "copy-to-clipboard"
          }
          onClick={onCopy}
          onMouseEnter={onMouseEnter}
          sx={{
            border: "1px solid",
            borderColor: (theme) =>
              justCopied
                ? theme.palette.none.main
                : theme.palette.background.lightest,
            borderRadius: (theme) => theme.spacing(0.5),
            backgroundColor: (theme) =>
              justCopied
                ? alpha(theme.palette.none.main, 0.1)
                : theme.palette.code.background,
            transition: ".300s ease-out",
            pointerEvents: justCopied ? "none" : "auto",
            "& .code-copy-icon": {
              color: (theme) =>
                justCopied
                  ? theme.palette.none.main
                  : theme.palette.text.primary,
            },
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.2),
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
            },
            "&:hover .code-copy-icon": {
              color: (theme) => theme.palette.primary.main,
            },
          }}
        >
          {justCopied ? (
            <CheckIcon className="code-copy-icon" fontSize="small" />
          ) : (
            <CopyIcon className="code-copy-icon" fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CodeCopyButton;
